import React, { useState } from "react";
import { Box, Card, CardContent, Grid, IconButton, TextField, Tooltip, Typography } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import { useTranslation } from "react-i18next";
import { useLoginStatus } from "../../../../context/LoginStatusContext";
import { useAlert } from "../../../../context/AlertContext";
import { apiRequest } from "../../../../utils/api";

const OrganizationInfo = () => {
  const { t } = useTranslation();
  const { user, refreshUser } = useLoginStatus();
  const { setAlert } = useAlert();
  const [editMode, setEditMode] = useState(false);
  const [organizationName, setOrganizationName] = useState("");
  const [saving, setSaving] = useState(false);

  if (!user?.enterprise) {
    return <Typography>{t("preferences.enterprise.noEnterpriseInfo")}</Typography>;
  }

  const { organization, role } = user.enterprise;
  const isOwner = role === "owner";

  // 編集モードに入るときに現在の組織名をセット
  const handleEdit = () => {
    setOrganizationName(organization.name);
    setEditMode(true);
  };

  // 編集をキャンセル
  const handleCancel = () => {
    setEditMode(false);
  };

  // 組織名を更新
  const handleSave = async () => {
    if (!organizationName.trim()) {
      setAlert("error", t("preferences.enterprise.error.nameRequired"));
      return;
    }

    try {
      setSaving(true);

      await apiRequest(`/api/v1/enterprise/organizations/${organization.uuid}/`, "PATCH", {
        name: organizationName.trim(),
      });

      setAlert("success", t("preferences.enterprise.success.updateName"));
      setEditMode(false);
      setSaving(false);

      // ユーザー情報を更新して新しい組織名を反映
      refreshUser();
    } catch (error) {
      console.error("組織名更新エラー:", error);
      setAlert("error", t("preferences.enterprise.error.updateName"));
      setSaving(false);
    }
  };

  return (
    <Box>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12 }}>
          <Card>
            <CardContent>
              {/* 組織名 - 編集モードによって表示を切り替え */}
              {editMode ? (
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <TextField
                    fullWidth
                    label={t("preferences.enterprise.organizationName")}
                    value={organizationName}
                    onChange={(e) => setOrganizationName(e.target.value)}
                    sx={{ mr: 1 }}
                    autoFocus
                  />
                  <Box>
                    <IconButton color="primary" onClick={handleSave} disabled={saving}>
                      <SaveIcon />
                    </IconButton>
                    <IconButton color="default" onClick={handleCancel} disabled={saving}>
                      <CancelIcon />
                    </IconButton>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <Typography variant="subtitle1" color="primary">
                    {organization.name}
                  </Typography>
                  {isOwner && (
                    <Tooltip title={t("preferences.enterprise.editName")}>
                      <IconButton size="small" onClick={handleEdit} sx={{ ml: 1 }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              )}

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {t("preferences.enterprise.organizationId")}
                </Typography>
                <Typography variant="body1">{organization.uuid}</Typography>
              </Box>

              <Box sx={{}}>
                <Typography variant="body2" color="text.secondary">
                  {t("preferences.enterprise.yourRole")}
                </Typography>
                <Typography variant="body1">
                  {t(
                    `preferences.enterprise.role.${role === "owner" ? "owner" : role === "manager" ? "manager" : "member"}`
                  )}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default OrganizationInfo;
